<script setup>
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  shallowRef,
  watch,
  getCurrentInstance,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

// O `t=` existe para furar cache, mas vinha de `Date.now()` DENTRO de um
// computed que depende da prop reativa: cada atualização da mensagem (o ack de
// entrega/leitura do WhatsApp troca o objeto do anexo) gerava um `t=` novo, o
// `<source src>` mudava e o elemento RECARREGAVA — abortando o play() que ainda
// estava carregando. Era o `AbortError` do iPad: lá o play demora segundos
// (o iOS ignora preload), então sobra janela para o ack chegar no meio.
// Agora o carimbo só é refeito quando o arquivo realmente muda.
const srcUrl = shallowRef(timeStampAppendedURL(attachment.dataUrl));

const { t } = useI18n();

// Nenhuma fonte tocou: sem isto o navegador só descarta os candidatos em
// silêncio — o mesmo silêncio do bug original, agora com mais código atrás.
const falhouAoTocar = ref(false);

watch(
  () => attachment.dataUrl,
  novaUrl => {
    srcUrl.value = timeStampAppendedURL(novaUrl);
    falhouAoTocar.value = false;
  }
);

// A segunda fonte precisa da MESMA estabilidade da primeira: derivada de um
// valor, nunca da identidade do objeto da prop (que a store troca a cada ack de
// entrega/leitura do WhatsApp). Se o `src` da fonte AAC mudar no meio do
// carregamento, o elemento recarrega e aborta o play() — o bug que esta feature
// existe para eliminar.
const aacUrl = shallowRef(attachment.aacUrl);

watch(
  () => attachment.aacUrl,
  novaUrl => {
    aacUrl.value = novaUrl;
    falhouAoTocar.value = false;
  }
);

const audioPlayer = useTemplateRef('audioPlayer');
const waveformRef = useTemplateRef('waveformRef');

const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

const onLoadedMetadata = () => {
  duration.value = audioPlayer.value?.duration;
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// Progress percentage (0 to 1)
const progress = computed(() => {
  if (!duration.value) return 0;
  return currentTime.value / duration.value;
});

// Generate pseudo-random waveform bars based on attachment data
const waveformBars = computed(() => {
  const numBars = 30;
  const bars = [];
  // Use attachment dataUrl as seed for consistent random heights
  const seed = attachment.dataUrl?.length || 42;
  for (let i = 0; i < numBars; i += 1) {
    // Generate pseudo-random height between 8 and 24 pixels
    const randomFactor = Math.sin(seed * i * 0.1) * 0.5 + 0.5;
    const height = 8 + randomFactor * 16;
    bars.push(Math.round(height));
  }
  return bars;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  duration.value = audioPlayer.value?.duration;
  audioPlayer.value.playbackRate = playbackSpeed.value;
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    // o estado cai sozinho pelo @pause do elemento
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const seekByClick = event => {
  if (!waveformRef.value || !duration.value) return;
  const rect = waveformRef.value.getBoundingClientRect();
  const clickPosition = (event.clientX - rect.left) / rect.width;
  const time = clickPosition * duration.value;
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

// `isPlaying` segue os eventos REAIS do elemento, nunca o nosso palpite. O
// helper global (SingleAudioPlaybackHelper) toca e pausa o áudio direto no DOM,
// sem passar por aqui; marcar na mão fazia o botão mentir e o toque seguinte
// cair no ramo errado — o cliente clicava e nada acontecia.
const onPlay = () => {
  isPlaying.value = true;
};

const onPause = () => {
  isPlaying.value = false;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    return;
  }

  // Emit event to pause all other audio
  emitter.emit('pause_playing_audio', uid);
  // O play() devolve promise: no iOS ela demora (não há preload) e pode ser
  // recusada. Sem tratar, a rejeição sumia e o estado ficava travado.
  const iniciado = audioPlayer.value.play();
  if (iniciado?.catch) {
    iniciado.catch(() => {
      isPlaying.value = false;
    });
  }
};

// O elemento dispara `error` quando esgota TODAS as <source> sem conseguir
// decodificar nenhuma. Mostramos uma frase curta e humana — nada de nome de
// erro técnico, aquilo era instrumentação de diagnóstico e já saiu.
const onErro = () => {
  falhouAoTocar.value = true;
  isPlaying.value = false;
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
    @play="onPlay"
    @pause="onPause"
    @error="onErro"
  >
    <!-- O `type` vem do backend (`source_type`), já mapeado para o MIME do
         container. Sem ele o Safari só descobre que não toca Opus depois de
         baixar e FALHAR — e quando ele trava sem emitir erro nunca chega na
         segunda fonte. Com o `type`, a decisão sai da heurística de erro e vai
         para o canPlayType. -->
    <source :src="srcUrl" :type="attachment.sourceType" />
    <!-- O Safari do iOS nao decodifica Opus em <audio>: ele ignora a fonte acima
         e pede esta, que o backend converte sob demanda. O Chrome toca a primeira
         e nunca encosta no endpoint. Quem escolhe e o navegador — detectar
         user-agent envelhece mal e o iPadOS se anuncia como Mac. -->
    <source v-if="aacUrl" :src="aacUrl" type="audio/mp4" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-2xl w-full p-2 bg-n-alpha-white/80 backdrop-blur-sm flex flex-col border border-n-container/50 shadow-sm min-w-[200px] max-w-[280px]"
  >
    <div class="flex gap-2 items-center">
      <!-- Play/Pause Button -->
      <button
        class="w-10 h-10 rounded-full bg-n-brand text-white flex items-center justify-center shrink-0 hover:bg-n-brand/90 transition-colors shadow-sm"
        @click="playOrPause"
      >
        <Icon v-if="isPlaying" class="size-5" icon="i-lucide-pause" />
        <Icon v-else class="size-5 ml-0.5" icon="i-lucide-play" />
      </button>

      <!-- Waveform & Progress -->
      <div class="flex-1 flex flex-col gap-1">
        <!-- Waveform Bars (clickable) -->
        <div
          ref="waveformRef"
          class="flex items-end gap-0.5 h-6 cursor-pointer"
          @click="seekByClick"
        >
          <div
            v-for="(height, i) in waveformBars"
            :key="i"
            class="w-1 rounded-full transition-all duration-100"
            :class="
              i / waveformBars.length <= progress
                ? 'bg-n-brand'
                : 'bg-n-slate-8'
            "
            :style="{ height: height + 'px' }"
          />
        </div>

        <!-- Time -->
        <div
          class="flex items-center justify-between text-[10px] text-n-slate-11 tabular-nums"
        >
          <span>{{ formatTime(currentTime) }}</span>
          <span>{{ formatTime(duration) }}</span>
        </div>
      </div>

      <!-- Speed -->
      <button
        class="text-[10px] font-medium text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3 px-1.5 py-0.5 rounded-full transition-colors"
        @click="changePlaybackSpeed"
      >
        {{ playbackSpeedLabel }}
      </button>
    </div>

    <!-- Falha tem que ser visível: o silêncio era o bug. -->
    <div
      v-if="falhouAoTocar"
      class="mt-2 pt-2 border-t border-n-weak text-xs text-n-ruby-11 leading-relaxed"
    >
      {{ t('CONVERSATION.AUDIO_PLAYBACK_FAILED') }}
    </div>

    <!-- Transcription (if available) -->
    <div
      v-if="attachment.transcribedText"
      class="mt-2 pt-2 border-t border-n-weak text-xs text-n-slate-11 break-words leading-relaxed"
    >
      {{ attachment.transcribedText }}
    </div>

    <!-- Hidden seek slider for accessibility -->
    <input
      type="range"
      min="0"
      :max="duration"
      :value="currentTime"
      class="sr-only"
      @input="seek"
    />
  </div>
</template>
